import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Alert, Box, Card, CardContent, Chip, Grid, IconButton, Tab, Tabs, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { PieChart } from "@mui/x-charts/PieChart";
import { getSendDetail, ListSendsRow, SendQueryParams } from "services/push-automation.service";
import { getGroupNames, getProgramNames } from "services/programs.service";
import { getBotChannelNames } from "data/bots-channels";

// Resuelve una lista de ids a "Nombre (id)" en una sola llamada batch — usado por la
// vista de solo lectura de configuracion para no mostrar ids crudos sin contexto.
function useNamesLabel(
  ids: (string | number)[] | null | undefined,
  fetchNames: (ids: (string | number)[]) => Promise<{ data: { id: string | number; name: string }[] }>,
): string {
  const [label, setLabel] = useState("");

  useEffect(() => {
    if (!ids || !ids.length) {
      setLabel("");
      return;
    }
    let cancelled = false;
    fetchNames(ids)
      .then(({ data }) => {
        if (cancelled) return;
        const names = new Map(data.map((d) => [String(d.id), d.name]));
        setLabel(ids.map((id) => `${names.get(String(id)) ?? "?"} (${id})`).join(", "));
      })
      .catch(() => {
        if (!cancelled) setLabel(ids.join(", "));
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(ids)]);

  return label;
}

function ConfigField({ label, value }: { label: string; value: string }) {
  if (!value) return null;
  return (
    <Grid item xs={12} sm={6} md={4}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Grid>
  );
}

// Vista de solo lectura de la configuracion que realmente se uso para armar la query
// de elegibilidad (ver getWAEligibleUsers) — no reconstruye el formulario de creacion
// completo (sesion/media/mensaje no quedan guardados de forma reconstruible hoy), pero
// muestra todo lo que decide A QUIEN le llega el envio: programas, bots, edad, filtros.
function SendConfigCard({ row }: { row: ListSendsRow }) {
  const qp = (row.query_params ?? {}) as SendQueryParams;
  const programLabel = useNamesLabel(row.program_ids, getProgramNames);
  const botLabel = useNamesLabel(qp.botChannelIds, (ids) => getBotChannelNames(ids as string[]).then((r) => ({ data: r.data.map((b) => ({ id: b.id, name: `${b.bot_name} (${b.phone_number})` })) })));

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="subtitle1" gutterBottom>
          Configuración del envío
        </Typography>
        <Grid container spacing={2}>
          <ConfigField label="Canal" value={row.channel} />
          <ConfigField label="Fecha de envío" value={row.date_to_send ?? ""} />
          <ConfigField label="Templates / push" value={row.templates.length ? row.templates.join(", ") : (row.push_name ?? "")} />
          <ConfigField label="Programas" value={programLabel || (row.program_ids ? row.program_ids.join(", ") : "")} />
          <ConfigField label="Bots (bot_channel_ids)" value={botLabel || (qp.botChannelIds ? qp.botChannelIds.join(", ") : "")} />
          <ConfigField label="Usuarios (user_channel_row_ids)" value={qp.userChannelRowIds ? qp.userChannelRowIds.join(", ") : ""} />
          <ConfigField label="Usuarios (user_channels, teléfonos)" value={qp.userChannels ? qp.userChannels.join(", ") : ""} />
          <ConfigField label="Edad mínima" value={row.age_min !== null ? String(row.age_min) : ""} />
          <ConfigField label="Edad máxima" value={row.age_max !== null ? String(row.age_max) : ""} />
          <ConfigField label="Límite" value={qp.limit !== null && qp.limit !== undefined ? String(qp.limit) : "Sin límite"} />
          <ConfigField label="Offset" value={qp.offset !== null && qp.offset !== undefined ? String(qp.offset) : ""} />
          <ConfigField label="Order by" value={qp.orderBy ?? ""} />
          <ConfigField label="Incluir suprimidos" value={qp.sendSuppressed ? `Sí (${qp.suppressedDays ?? "todos"} días)` : ""} />
          <ConfigField label="Rango inactivo" value={qp.inactiveRange ? "Sí" : ""} />
        </Grid>
      </CardContent>
    </Card>
  );
}

type BreakdownRow = { key: string; name: string; success: number; fail: number; total: number };

// Resuelve nombres para TODAS las keys de un desglose (group_id o bot_channel) en UNA
// sola llamada batch (no una por key) — cae al id crudo si el lookup falla (ej. el
// usuario no es admin en ese servicio) o si ese id no vino en la respuesta.
function useBreakdownNames(
  breakdown: Record<string, { success: number; fail: number }> | undefined,
  fetchNames: (keys: string[]) => Promise<Map<string, string>>,
): BreakdownRow[] {
  const [rows, setRows] = useState<BreakdownRow[]>([]);

  useEffect(() => {
    if (!breakdown) {
      setRows([]);
      return;
    }
    let cancelled = false;
    const entries = Object.entries(breakdown);
    const knownKeys = entries.filter(([key]) => key !== "unknown").map(([key]) => key);
    fetchNames(knownKeys)
      .catch(() => new Map<string, string>())
      .then((names) => {
        if (cancelled) return;
        setRows(
          entries.map(([key, counts]) => ({
            key,
            name: key === "unknown" ? "Sin dato" : (names.get(key) ?? key),
            success: counts.success,
            fail: counts.fail,
            total: counts.success + counts.fail,
          })),
        );
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breakdown]);

  return rows;
}

const breakdownColumns: GridColDef<BreakdownRow>[] = [
  { field: "name", headerName: "Nombre", flex: 1 },
  { field: "success", headerName: "Éxitos", flex: 0.5 },
  { field: "fail", headerName: "Fallos", flex: 0.5 },
  { field: "total", headerName: "Total", flex: 0.5 },
];

function BreakdownTable({ rows, emptyLabel }: { rows: BreakdownRow[]; emptyLabel: string }) {
  if (!rows.length) return <Typography color="text.secondary">{emptyLabel}</Typography>;
  return (
    <DataGrid
      rows={rows}
      columns={breakdownColumns}
      getRowId={(r) => r.key}
      autoHeight
      hideFooter={rows.length <= 10}
      pageSizeOptions={[10, 25]}
      initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
    />
  );
}

const STATUS_COLORS = { success: "#4CAF50", fail: "#F44336", skipped: "#FF9800" };

function StatTile({ label, value }: { label: string; value: string | number }) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h5">{value}</Typography>
      </CardContent>
    </Card>
  );
}

const idColumns: GridColDef[] = [{ field: "id", headerName: "User ID", flex: 1 }];

function IdListTable({ ids }: { ids: number[] }) {
  const rows = ids.map((id) => ({ id }));
  return (
    <DataGrid
      rows={rows}
      columns={idColumns}
      autoHeight
      pageSizeOptions={[10, 25, 50]}
      initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
    />
  );
}

function SkippedTable({ skippeds }: { skippeds: Record<string, number[]> }) {
  const rows = Object.entries(skippeds).flatMap(([reason, ids]) => ids.map((id) => ({ id: `${reason}-${id}`, reason, userId: id })));
  const columns: GridColDef[] = [
    { field: "reason", headerName: "Motivo", flex: 1 },
    { field: "userId", headerName: "User ID", flex: 1 },
  ];
  return (
    <DataGrid
      rows={rows}
      columns={columns}
      autoHeight
      pageSizeOptions={[10, 25, 50]}
      initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
    />
  );
}

function SendReportPage() {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const [row, setRow] = useState<ListSendsRow | null>(null);
  const [tab, setTab] = useState(0);

  useEffect(() => {
    getSendDetail(id).then((res) => setRow(res.data));
  }, [id]);

  // Los hooks de abajo deben correr siempre, sin importar si `row` ya cargo — el
  // early-return de renderizado va despues, no antes, para no violar las Rules of Hooks.
  const groupRows = useBreakdownNames(row?.report?.by_group, async (ids) => {
    if (!ids.length) return new Map();
    const { data } = await getGroupNames(ids);
    return new Map(data.map((g) => [String(g.id), g.name]));
  });
  const botRows = useBreakdownNames(row?.report?.by_bot, async (ids) => {
    if (!ids.length) return new Map();
    const { data } = await getBotChannelNames(ids);
    return new Map(data.map((b) => [b.id, `${b.bot_name} (${b.phone_number})`]));
  });

  if (!row) return null;

  const report = row.report;
  const success = report?.success ?? 0;
  const fail = report?.fail ?? 0;
  const skipped = report?.skipped ?? 0;
  const total = success + fail + skipped;
  const rate = success + fail === 0 ? 0 : Math.round((success / (success + fail)) * 100);
  const successIds = report?.successes ?? [];
  const errorIds = report?.errors ?? [];
  const skippeds = report?.skippeds ?? {};
  const skippedCount = Object.values(skippeds).reduce((acc, ids) => acc + ids.length, 0);

  const pieData = [
    { id: "success", value: success, label: "Exitosos", color: STATUS_COLORS.success },
    { id: "fail", value: fail, label: "Fallidos", color: STATUS_COLORS.fail },
    ...(skippedCount > 0 ? [{ id: "skipped", value: skippedCount, label: "Omitidos", color: STATUS_COLORS.skipped }] : []),
  ];

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <IconButton onClick={() => history.push("/admin/push/calendar")} aria-label="volver">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5">Reporte de envío #{row.id}</Typography>
        {report?.dry && <Chip label="DRY" color="info" size="small" />}
        <Chip label={row.status} size="small" />
      </Box>

      <SendConfigCard row={row} />

      {!report ? (
        <Alert severity="info">Este envío aún no tiene reporte.</Alert>
      ) : (
        <>
          {report.dry && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Esto fue una prueba en modo DRY — no se envió nada real. Los conteos reflejan la simulación.
            </Alert>
          )}
          {report.aborted && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              El envío se abortó por fallos de red consecutivos ({report.aborted.reason}) —{" "}
              {report.aborted.remainingUsers} usuarios no se llegaron a intentar.
            </Alert>
          )}

          <Grid container spacing={2} mb={2}>
            <Grid item xs={6} sm={3}>
              <StatTile label="Total" value={total} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatTile label="Éxitos" value={success} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatTile label="Fallos" value={fail} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatTile label="Tasa de éxito" value={`${rate}%`} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatTile label="Corridas en DRY" value={report.run_history?.dry_runs ?? 0} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatTile label="Corridas reales" value={report.run_history?.real_runs ?? 0} />
            </Grid>
          </Grid>

          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} md={5}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Distribución
                  </Typography>
                  <Box display="flex" justifyContent="center">
                    <PieChart series={[{ data: pieData, innerRadius: 40 }]} height={220} legend={{ hidden: true }} />
                  </Box>
                  <Box display="flex" flexWrap="wrap" gap={2} justifyContent="center" mt={1}>
                    {pieData.map((d) => (
                      <Box key={d.id} display="flex" alignItems="center" gap={0.5}>
                        <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: d.color }} />
                        <Typography variant="body2">
                          {d.label} ({d.value})
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={7}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>
                        Por grupo / programa
                      </Typography>
                      <BreakdownTable rows={groupRows} emptyLabel="Sin desglose por grupo." />
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>
                        Por bot
                      </Typography>
                      <BreakdownTable rows={botRows} emptyLabel="Sin desglose por bot." />
                    </CardContent>
                  </Card>
                </Grid>
                {report.run_history && report.run_history.runs.length > 0 && (
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle1" gutterBottom>
                          Historial de corridas
                        </Typography>
                        <DataGrid
                          rows={report.run_history.runs.map((r, i) => ({ id: i, ...r }))}
                          columns={[
                            {
                              field: "ran_at",
                              headerName: "Fecha",
                              flex: 1,
                              valueFormatter: (value) => new Date(value as string).toLocaleString(),
                            },
                            {
                              field: "dry",
                              headerName: "Tipo",
                              flex: 0.5,
                              renderCell: (params) => (
                                <Chip label={params.value ? "DRY" : "Real"} size="small" color={params.value ? "info" : "success"} />
                              ),
                            },
                            { field: "success", headerName: "Éxitos", flex: 0.5 },
                            { field: "fail", headerName: "Fallos", flex: 0.5 },
                          ]}
                          autoHeight
                          hideFooter={report.run_history.runs.length <= 10}
                          initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>

          <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 1 }}>
            <Tab label={`Exitosos (${successIds.length})`} />
            <Tab label={`Fallidos (${errorIds.length})`} />
            <Tab label={`Omitidos (${skippedCount})`} />
          </Tabs>
          {tab === 0 && <IdListTable ids={successIds} />}
          {tab === 1 && <IdListTable ids={errorIds} />}
          {tab === 2 && <SkippedTable skippeds={skippeds} />}
        </>
      )}
    </Box>
  );
}

export default SendReportPage;
